import { ownAmount, type BudgetPeriod, type PeriodItem, type PeriodSection, type PlanShare } from '@fin/shared';
import { Fragment, useState } from 'react';
import { type TxDefaults } from '../transactions/public.ts';

import { Button, Icon, Tile, cx, toast } from '../../components/ui.tsx';
import { del, patch, post, qs } from '../../lib/api.ts';
import { useCategories, useRates, useWallets } from '../../lib/data.ts';
import { money, plural, rub } from '../../lib/format.ts';
import { categoryColor, categoryIcon, kindTint } from '../../lib/icons.tsx';

import { invalidateMoney } from '../../lib/query.ts';
import { Link } from '../../lib/router.tsx';
import { type Line, emptyLine, sectionTitles, isHeld, canHold, RESERVE_NAME } from './model.tsx';
import { usePlanCheck } from './payment.tsx';
import { SelectRow, LineForm, LineModal, ReorderLines } from './editor.tsx';

export function HoldBlock({ it, check }: { it: PeriodItem; check: ReturnType<typeof usePlanCheck> }) {
  const wallets = useWallets();
  const main = check.main;
  const options = (wallets.data ?? []).filter((w) => !w.archived && w.id !== main?.id && w.currency === it.currency);
  const named = options.find((w) => w.name.trim().toLowerCase() === RESERVE_NAME.toLowerCase());
  const [walletId, setWalletId] = useState<number | null>(options.find((w) => w.id === it.reserveWalletId)?.id ?? named?.id ?? (named ? null : -1));
  const busy = check.busy === it.id;
  if (isHeld(it)) {
    const w = wallets.data?.find((x) => x.id === it.reserveWalletId);
    return (
      <div className="hold-block">
        <span className="hold-text">Отложено <span className="money">{rub(it.held)}</span> на «{w?.name ?? 'счёт'}». Отметка строки спишет оплату с этого счёта.</span>
        <Button size="sm" busy={busy} onClick={() => void check.release(it)}>Вернуть на основной</Button>
      </div>
    );
  }
  if (!main || main.currency !== it.currency || ownAmount(it) <= 0) return null;
  // until there is a wallet for bills, the list offers to create «Обязательные траты»
  const choices = [...options.map((w) => ({ id: w.id, name: w.name })), ...(named ? [] : [{ id: -1, name: `Новый счёт «${RESERVE_NAME}»` }])];
  return (
    <div className="hold-block">
      <span className="hold-text">Ещё не оплачено? Отложите <span className="money">{rub(ownAmount(it))}</span> с «{main.name}» до оплаты.</span>
      <div className="qa-rows">
        <SelectRow label="Отложить на счёт" value={walletId} placeholder="Выбрать" tile={<Tile icon="transfer" color={kindTint.transfer} />}
          options={choices} onChange={setWalletId} />
      </div>
      <Button size="sm" busy={busy} disabled={walletId === null} onClick={() => walletId !== null && void check.hold(it, walletId === -1 ? 'new' : walletId)}>Отложить</Button>
    </div>
  );
}

/** «ждут: Тимур, Влад» while people still owe their parts of a shared line, «доли собраны» after */
function sharesNote(shares: PlanShare[]) {
  const waiting = shares.filter((s) => s.debtId ? (s.remaining ?? 0) > 0.005 : true).map((s) => s.contactName || '—');
  return waiting.length ? `ждут: ${waiting.join(', ')}` : 'доли собраны';
}

export function PlanList({ period, section, onPay, onChanged }: { period: BudgetPeriod; section: PeriodSection; onPay: (d: TxDefaults) => void; onChanged: () => void }) {
  const rates = useRates();
  const wallets = useWallets();
  const check = usePlanCheck(period, onPay, onChanged);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [ordering, setOrdering] = useState(false);
  const items = period.items.filter((i) => i.section === section);
  const editingItem = typeof editing === 'number' ? items.find((i) => i.id === editing) : undefined;
  const counted = items.filter((i) => i.included);
  const paid = counted.filter((i) => i.paid);
  // unpaid lines first, by size; paid ones sink to the bottom
  const sorted = [...items].sort((a, b) => Number(a.paid) - Number(b.paid) || Number(!a.included) - Number(!b.included) || a.sort - b.sort);
  const total = counted.reduce((s, i) => s + (rates.toBase(ownAmount(i), i.currency) ?? 0), 0);
  const save = (id: number, d: Partial<Line>) => patch(`/period-items/${id}`, d).then(onChanged);
  const wrap = async (p: Promise<unknown>) => { try { await p; } catch (e) { toast((e as Error).message, 'error'); throw e; } };

  return (
    <section className={cx('section plan', `plan-${section}`)} aria-labelledby={`plan-${section}`}>
      <div className="section-head">
        <h2 id={`plan-${section}`}>{sectionTitles[section]}</h2>
        <span className="section-aside">
          {section !== 'foreign' && counted.length ? <span className="muted small">оплачено {paid.length} из {counted.length}</span> : null}
          <strong className="money">{rub(total)}</strong>
        </span>
      </div>
      {ordering ? (
        <ReorderLines items={[...items].sort((a, b) => a.sort - b.sort || a.id - b.id)} onDone={() => setOrdering(false)}
          onSave={async (ids) => { await post(`/periods/${period.id}/reorder`, { ids }); onChanged(); }} />
      ) : <>
      <ul className="plan-lines">
        {sorted.map((it) => (
          <li key={it.id} className={cx('plan-line', it.paid && 'is-paid', !it.included && 'is-off', (!!it.debtId || it.highlight) && 'is-debt')}>
            {/* the check pays the bill (from the wallet its money waits on, when it was put aside) or takes the payment back */}
            <button type="button" role="checkbox" aria-checked={it.paid} className={cx('plan-check', isHeld(it) && 'is-held', check.busy === it.id && 'is-busy')}
              aria-label={it.paid ? `Отменить оплату: ${it.name}` : isHeld(it) ? `Подтвердить оплату с отложенных: ${it.name}` : `Оплатить: ${it.name}`}
              disabled={!it.included || check.busy !== null || section === 'foreign'}
              onClick={() => void check.toggle(it)}>
              {isHeld(it) && !it.paid
                ? <svg viewBox="0 0 24 24" aria-hidden><path d="M12 7v5l3.5 2" /></svg>
                : <svg viewBox="0 0 24 24" aria-hidden><path d="M6 12.5l4 4 8-9" /></svg>}
            </button>
            <button type="button" className="plan-line-main" onClick={() => setEditing(it.id)}>
              <span className="plan-name">{it.name}</span>
              <span className="plan-meta">
                {[
                  it.walletId ? `перевод на ${wallets.data?.find((w) => w.id === it.walletId)?.name ?? 'счёт'}` : it.debtId || it.highlight ? 'платёж по долгу' : it.goalId ? 'в накопления' : null,
                  isHeld(it) ? <span key="held" className="plan-held">отложено на «{wallets.data?.find((w) => w.id === it.reserveWalletId)?.name ?? 'счёт'}»</span> : null,
                  it.spent > 0 && !it.paid ? <span key="spent">внесено <span className="money">{rub(it.spent)}</span></span> : null,
                  it.shares?.length ? <span key="shares">ваша часть из <span className="money">{money(it.amount, it.currency)}</span>, {sharesNote(it.shares)}</span> : null,
                  !it.included ? 'не учитывается' : null,
                ].filter(Boolean).map((part, i) => <Fragment key={i}>{i ? ' · ' : ''}{part}</Fragment>)}
              </span>
            </button>
            <span className="plan-amount">
              <span className="money">{money(it.paid && it.spent > 0 ? it.spent : ownAmount(it), it.paid && it.spent > 0 ? rates.base : it.currency)}</span>
              {it.currency !== rates.base ? <span className="muted small money">{rub(rates.toBase(ownAmount(it), it.currency))}</span> : null}
            </span>
          </li>
        ))}
        {!items.length ? <li className="muted small plan-empty">Строк нет.</li> : null}
      </ul>
      <div className="plan-tools">
        <button type="button" className="btn btn-ghost btn-sm add-line" onClick={() => setEditing('new')}><Icon name="plus" size={16} /> Добавить строку</button>
        {items.length > 1 ? <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(null); setOrdering(true); }}><Icon name="grip" size={16} /> Порядок</button> : null}
      </div>
      </>}
      {editing === 'new' ? (
        <LineModal section={section} onClose={() => setEditing(null)}
          onSubmit={(d) => wrap(post(`/periods/${period.id}/items`, { ...d, section }).then(onChanged))} />
      ) : null}
      {editingItem ? (
        <LineModal key={editingItem.id} section={section} initial={editingItem} onClose={() => setEditing(null)}
          extra={canHold(editingItem) && !editingItem.paid ? <HoldBlock it={editingItem} check={check} /> : null}
          onSubmit={(d) => wrap(save(editingItem.id, d))}
          onDelete={async () => {
            // the server returns money put aside for an unpaid line to the main account
            await wrap(del(`/period-items/${editingItem.id}`).then(onChanged));
            if (isHeld(editingItem)) { invalidateMoney(); toast(`${rub(editingItem.held)} возвращено на основной счёт`); }
          }} />
      ) : null}
    </section>
  );
}

export function Spending({ period, onPay, onChanged }: { period: BudgetPeriod; onPay: (d: TxDefaults) => void; onChanged: () => void }) {
  const cats = useCategories();
  const rates = useRates();
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const limits = period.items.filter((i) => i.section === 'optional');
  const s = period.summary;
  const total = s.optional + s.unplanned;
  const range = { from: period.startDate, to: period.endDate };
  const wrap = async (p: Promise<unknown>) => { try { await p; } catch (e) { toast((e as Error).message, 'error'); throw e; } };

  return (
    <section className="section spending" aria-labelledby="spending-title">
      <div className="section-head">
        <h2 id="spending-title">Траты</h2>
        <strong className="money section-aside">{rub(total)}</strong>
      </div>
      <ul className="cat-lines">
        {limits.map((it) => {
          if (editing === it.id) {
            return (
              <li key={it.id}>
                <LineForm initial={it} section="optional" onCancel={() => setEditing(null)}
                  onSubmit={async (d) => { await wrap(patch(`/period-items/${it.id}`, d).then(onChanged)); setEditing(null); }}
                  onDelete={async () => { await wrap(del(`/period-items/${it.id}`).then(onChanged)); setEditing(null); }} />
              </li>
            );
          }
          const cat = cats.data?.find((c) => c.id === it.categoryId) ?? cats.data?.find((c) => c.kind === 'expense' && c.name.toLowerCase() === it.name.toLowerCase());
          const planned = rates.toBase(ownAmount(it), it.currency) ?? ownAmount(it);
          const ratio = planned > 0 ? it.spent / planned : 0;
          const overLimit = it.spent > planned + 0.5;
          return (
            <li key={it.id} className={cx('cat-line', 'has-limit', !it.included && 'is-off')}>
              <button type="button" className="cat-line-main" onClick={() => setEditing(it.id)}>
                <Tile icon={cat ? categoryIcon(cat) : 'folder'} color={cat ? categoryColor(cat) : '#8F8A7E'} image={cat?.iconImage} />
                <span className="cat-text">
                  <span className="cat-name">{it.name}</span>
                  <span className="cat-meta">{overLimit ? <span className="neg">перерасход <span className="money">{rub(it.spent - planned)}</span></span> : it.paid ? 'закрыто' : <>осталось <span className="money">{rub(planned - it.spent)}</span></>}</span>
                </span>
                <span className="cat-amount"><span className="money">{rub(it.spent)}</span><span className="muted small money">из {rub(planned)}</span></span>
              </button>
              <span className={cx('limit-track', overLimit && 'over')} role="progressbar" aria-label={`${it.name}: потрачено из лимита`}
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(1, ratio) * 100)}>
                <i style={{ width: `${Math.min(1, ratio) * 100}%` }} />
              </span>
              {!it.paid && it.included ? (
                <button type="button" className="icon-btn limit-add" aria-label={`Добавить трату: ${it.name}`} onClick={() => onPay({ mode: 'expense', categoryId: it.categoryId ?? cat?.id, periodItemId: it.id })}>
                  <Icon name="plus" size={18} />
                </button>
              ) : <span className="limit-add" aria-hidden />}
            </li>
          );
        })}
        {s.unplannedByCategory.map((u) => {
          const cat = cats.data?.find((c) => c.id === u.categoryId);
          return (
            <li key={`u${u.categoryId}`} className="cat-line">
              <Link to={`/transactions${qs({ category: u.categoryId, ...range })}`} className="cat-line-main">
                <Tile icon={cat ? categoryIcon(cat) : 'folder'} color={cat ? categoryColor(cat) : '#8F8A7E'} image={cat?.iconImage} />
                <span className="cat-text">
                  <span className="cat-name">{u.name}</span>
                  <span className="cat-meta">{u.count} {plural(u.count, 'операция', 'операции', 'операций')}</span>
                </span>
                <span className="cat-amount"><span className="money">{rub(u.amount)}</span></span>
              </Link>
            </li>
          );
        })}
        {!limits.length && !s.unplannedByCategory.length ? <li className="muted small plan-empty">Трат пока нет. Операции периода появятся здесь сами.</li> : null}
      </ul>
      {editing === 'new' ? (
        <LineForm initial={{ id: 0, section: 'optional', name: '', amount: 0, currency: 'RUB', ...emptyLine }}
          section="optional" onCancel={() => setEditing(null)}
          onSubmit={async (d) => { await wrap(post(`/periods/${period.id}/items`, { ...d, section: 'optional' }).then(onChanged)); setEditing(null); }} />
      ) : (
        <button type="button" className="btn btn-ghost btn-sm add-line" onClick={() => setEditing('new')}><Icon name="plus" size={16} /> Лимит на категорию</button>
      )}
    </section>
  );
}
